import { BsExclamationCircleFill } from "react-icons/bs";
import { MdPerson } from "react-icons/md";
import SlidingModal from "../../SlidingModal/slidingModal";
import { PrimaryButton, SecondaryButton } from "../../../StyledCss/styledbtn";
import {
  StyledInput,
  StyledInputContainer,
  StyledInputGroup,
  StyledLabel,
} from "../../../StyledCss/styledInputs";
import {
  formatDuplicateReason,
  getAtsMatchBadgeClass,
  getAtsScoreColor,
  getImportMissingFields,
  getReviewFormErrors,
  isPdfResumeUrl,
  toEntryListString,
} from "../../../util/hiringUtils";
import "../ResumeReviewModal/index.css";
import "../CandidateDetailsPage/index.css";

const DETAIL_FIELDS = [
  { label: "Name",               key: "name" },
  { label: "Email",              key: "email" },
  { label: "Phone",              key: "phone" },
  { label: "Designation",        key: "designation" },
  { label: "Experience",         key: "experience" },
  { label: "Education",          key: "education" },
  { label: "Skills",             key: "skills" },
  { label: "Previous Companies", key: "previousCompanies" },
  { label: "Certifications",     key: "certifications" },
  { label: "Projects",           key: "projects" },
  { label: "Current CTC",        key: "currentCTC" },
  { label: "Expected CTC",       key: "expectedCTC" },
  { label: "Notice Period",      key: "noticePeriod" },
];

const REQUIRED_KEYS = ["name", "email", "phone"];

/* Read-only profile view — mirrors CandidateDetailsPage's info-card look,
 * so a pending import candidate reads the same as a saved application. */
const CandidateDetailsView = ({ form, missing }) => (
  <div className="cd-info-card" style={{ background: "#fff" }}>
    <p className="cd-info-card-title">
      <MdPerson size={13} style={{ marginRight: 5 }} />Candidate Details
    </p>
    <div className="cd-info-card-grid">
      {DETAIL_FIELDS.map(({ label, key }) => {
        const isMissing = REQUIRED_KEYS.includes(key) && missing.includes(key);
        return (
          <div className="cd-info-field" key={key}>
            <span className="cd-info-field-label">{label}</span>
            <span
              className="cd-info-field-value"
              style={isMissing ? { color: "#dc3545" } : {}}
            >
              {form?.[key]?.trim() || (isMissing ? "Missing" : "—")}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const firstDefined = (obj, keys) => {
  for (const k of keys) {
    if (obj?.[k]) return obj[k];
  }
  return null;
};

/* Shows which existing application this row was flagged as a duplicate of,
 * so the reviewer can decide whether to remove it or keep it deliberately. */
const DuplicateMatchBanner = ({ existingCandidate, duplicateReason }) => {
  const reason = formatDuplicateReason(duplicateReason);
  if (!existingCandidate && !reason) return null;

  const name  = firstDefined(existingCandidate, ["name", "candidate_name"]);
  const email = firstDefined(existingCandidate, ["email"]);
  const phone = firstDefined(existingCandidate, ["phone", "mobile"]);

  return (
    <div className="cd-info-card" style={{ background: "#fff8e1", borderColor: "#ffe28a" }}>
      <p className="cd-info-card-title" style={{ color: "#856404" }}>
        <BsExclamationCircleFill size={12} style={{ marginRight: 5 }} />
        {reason || "Matches Existing Candidate"}
      </p>
      {existingCandidate && (
        <div className="cd-info-card-grid">
          <div className="cd-info-field">
            <span className="cd-info-field-label">Name</span>
            <span className="cd-info-field-value">{name || "—"}</span>
          </div>
          <div className="cd-info-field">
            <span className="cd-info-field-label">Email</span>
            <span className="cd-info-field-value">{email || "—"}</span>
          </div>
          {phone && (
            <div className="cd-info-field">
              <span className="cd-info-field-label">Phone</span>
              <span className="cd-info-field-value">{phone}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ReviewFormField = ({ label, fieldKey, form, onChange, required = false, full = false, error }) => {
  const isMissing = required && !form[fieldKey]?.trim();
  const hasError = isMissing || !!error;
  return (
    <StyledInputGroup className={full ? "resume-review-field-full" : ""}>
      <StyledInputContainer>
        <StyledInput
          type="text"
          placeholder=" "
          value={form[fieldKey] || ""}
          onChange={(e) => onChange(fieldKey, e.target.value)}
          style={{ width: "100%" }}
          inputError={hasError}
        />
        <StyledLabel
          className="user-label"
          style={hasError ? { color: "#dc3545" } : {}}
        >
          {label}{required && " *"}
        </StyledLabel>
      </StyledInputContainer>
      {hasError && (
        <p className="ir-field-error">{isMissing ? `Missing ${label.toLowerCase()}` : error}</p>
      )}
    </StyledInputGroup>
  );
};

export const ImportStatusBadge = ({ status }) => {
  const labels = {
    READY:        "Ready",
    NEEDS_REVIEW: "Needs Review",
    FAILED:       "Failed",
    DUPLICATE:    "Duplicate",
  };
  return <span className="ir-status-text">{labels[status] || status}</span>;
};

export const ResumeImportReviewDrawer = ({
  candidate,
  form,
  onChange,
  onSave,
  onClose,
  isSaving,
  primaryColor,
  viewOnly = false,
  editable = true,
  onRequestEdit,
  serverErrors = {},
}) => {
  const missing = getImportMissingFields(form || {});
  const fieldErrors = getReviewFormErrors(form || {});
  const hasBlockingErrors = missing.length > 0 || Object.keys(fieldErrors).length > 0;

  return (
    <div className="ir-review-sliding-root">
      <SlidingModal
        isModalOpen={!!candidate}
        modalName={candidate?.name || "Review Candidate"}
        closeBtn={isSaving ? () => {} : onClose}
      >
        <div className="resume-review-modal" style={{ "--hiring-primary": primaryColor }}>

          <div className="resume-review-ats-banner">
            <div>
              <p className="resume-review-ats-label">ATS Score</p>
              <p
                className="resume-review-ats-score"
                style={{ color: getAtsScoreColor(candidate?.atsScore) }}
              >
                {candidate?.atsScore || 0}%
              </p>
            </div>
            <span className={getAtsMatchBadgeClass(candidate?.atsMatchLabel)}>
              {candidate?.atsMatchLabel || "—"}
            </span>
          </div>

          {missing.length > 0 && (
            <div className="ir-review-warn">
              <BsExclamationCircleFill size={14} style={{ flexShrink: 0 }} />
              <span>
                Required fields missing: <strong>{missing.join(", ")}</strong>
              </span>
            </div>
          )}

          {!viewOnly && Object.keys(fieldErrors).length > 0 && (
            <div className="ir-review-warn">
              <BsExclamationCircleFill size={14} style={{ flexShrink: 0 }} />
              <span>Please fix the highlighted fields before saving.</span>
            </div>
          )}

          <div className="resume-review-body">
            {viewOnly ? (
              <div className="resume-review-fields" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <DuplicateMatchBanner
                  existingCandidate={candidate?.existingCandidate}
                  duplicateReason={candidate?.duplicateReason}
                />
                <CandidateDetailsView form={form} missing={missing} />
              </div>
            ) : (
              <div className="resume-review-fields">
                <p className="resume-review-section-title">Candidate Details</p>
                <div className="resume-review-grid">
                  <ReviewFormField label="Name"               fieldKey="name"              form={form} onChange={onChange} required error={fieldErrors.name} />
                  <ReviewFormField label="Email"              fieldKey="email"             form={form} onChange={onChange} required error={fieldErrors.email || serverErrors.email} />
                  <ReviewFormField label="Phone"              fieldKey="phone"             form={form} onChange={onChange} required error={fieldErrors.phone} />
                  <ReviewFormField label="Designation"        fieldKey="designation"       form={form} onChange={onChange} error={fieldErrors.designation} />
                  <ReviewFormField label="Experience"         fieldKey="experience"        form={form} onChange={onChange} />
                  <ReviewFormField label="Education"          fieldKey="education"         form={form} onChange={onChange} />
                  <ReviewFormField label="Skills"             fieldKey="skills"            form={form} onChange={onChange} full />
                  <ReviewFormField label="Previous Companies" fieldKey="previousCompanies" form={form} onChange={onChange} full />
                  <ReviewFormField label="Certifications"     fieldKey="certifications"    form={form} onChange={onChange} full />
                  <ReviewFormField label="Projects"           fieldKey="projects"          form={form} onChange={onChange} full />
                  <ReviewFormField label="Current CTC"        fieldKey="currentCTC"        form={form} onChange={onChange} error={fieldErrors.currentCTC} />
                  <ReviewFormField label="Expected CTC"       fieldKey="expectedCTC"       form={form} onChange={onChange} error={fieldErrors.expectedCTC} />
                  <ReviewFormField label="Notice Period"      fieldKey="noticePeriod"      form={form} onChange={onChange} error={fieldErrors.noticePeriod} />
                </div>
              </div>
            )}

            <div className="resume-review-preview" style={viewOnly ? { flex: 1 } : {}}>
              <p className="resume-review-section-title">Resume Preview</p>
              {candidate?.resumeUrl && isPdfResumeUrl(candidate.resumeUrl, candidate.resumeName || candidate.resumeFileName) ? (
                <iframe
                  src={`${candidate.resumeUrl}#navpanes=0`}
                  title="Resume Preview"
                  className="resume-review-iframe"
                />
              ) : (
                <div className="resume-review-preview-empty">
                  {candidate?.resumeUrl
                    ? "Only PDF files can be viewed here."
                    : "Preview not available for this file type."}
                </div>
              )}
            </div>
          </div>

          <div className="resume-review-actions">
            <SecondaryButton type="button" onClick={onClose} disabled={isSaving}>
              {viewOnly ? "Close" : "Cancel"}
            </SecondaryButton>
            {viewOnly && editable && (
              <PrimaryButton type="button" onClick={onRequestEdit}>
                Fill Missing Info
              </PrimaryButton>
            )}
            {!viewOnly && (
              <PrimaryButton
                type="button"
                onClick={onSave}
                disabled={isSaving || hasBlockingErrors}
              >
                {isSaving ? "Saving…" : "Save Candidate"}
              </PrimaryButton>
            )}
          </div>
        </div>
      </SlidingModal>
    </div>
  );
};

export const buildReviewFormFromCandidate = (c) => ({
  name:              c.name              || "",
  email:             c.email             || "",
  phone:             c.phone             || "",
  skills:            c.skills            || "",
  experience:        c.experience        || "",
  education:         toEntryListString(c.education)         || "",
  previousCompanies: toEntryListString(c.previousCompanies) || "",
  certifications:    toEntryListString(c.certifications)    || "",
  projects:          toEntryListString(c.projects)          || "",
  designation:       c.designation       || "",
  currentCTC:        c.currentCTC        || "",
  expectedCTC:       c.expectedCTC       || "",
  noticePeriod:      c.noticePeriod      || "",
});
